import dagre from 'dagre';
import { Node, Edge } from '@xyflow/react';

export interface LayoutOptions {
  direction?: 'TB' | 'BT' | 'LR' | 'RL';
  nodeWidth?: number;
  nodeHeight?: number;
  rankSep?: number;
  nodeSep?: number;
}

const defaultOptions: Required<LayoutOptions> = {
  direction: 'TB', // Top to bottom (hierarchical)
  nodeWidth: 150,
  nodeHeight: 60,
  rankSep: 100, // Vertical spacing between levels
  nodeSep: 50, // Horizontal spacing between nodes
};

/**
 * Apply dagre layout algorithm to position nodes hierarchically
 * This is ideal for wormhole chains where k-space is the root
 */
export function applyDagreLayout<T extends Record<string, unknown> = Record<string, unknown>, U extends Record<string, unknown> = Record<string, unknown>>(
  nodes: Node<T>[],
  edges: Edge<U>[],
  options: LayoutOptions = {}
): Node<T>[] {
  const opts = { ...defaultOptions, ...options };

  // Create dagre graph
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: opts.direction,
    ranksep: opts.rankSep,
    nodesep: opts.nodeSep,
  });

  // Add nodes to dagre graph
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: opts.nodeWidth,
      height: opts.nodeHeight,
    });
  });

  // Add edges to dagre graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply calculated positions to nodes
  return nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    if (!nodeWithPosition) return node;

    return {
      ...node,
      position: {
        x: nodeWithPosition.x - opts.nodeWidth / 2,
        y: nodeWithPosition.y - opts.nodeHeight / 2,
      },
    };
  });
}

/**
 * Find the k-space entry point (highest security status system)
 * This will be used as the root for the layout
 */
export function findKSpaceEntry<T extends Record<string, unknown> & { securityStatus?: number | null; wormholeClass?: string | null }>(
  nodes: Node<T>[]
): string | null {
  // First, try to find a k-space system (one without wormhole class)
  const kSpaceSystems = nodes.filter((n) => !n.data.wormholeClass && n.data.securityStatus !== undefined);

  if (kSpaceSystems.length > 0) {
    // Return the one with highest security status
    return kSpaceSystems.reduce((highest, current) => {
      const currentSec = current.data.securityStatus ?? -10;
      const highestSec = highest.data.securityStatus ?? -10;
      return currentSec > highestSec ? current : highest;
    }).id;
  }

  // If no k-space, return the first node or null
  return nodes.length > 0 ? nodes[0].id : null;
}

/**
 * Create a hierarchical layout starting from a root node
 * Positions nodes in layers based on their distance from root
 */
export function applyHierarchicalLayout<T extends Record<string, unknown> = Record<string, unknown>>(
  nodes: Node<T>[],
  edges: Edge[],
  rootId?: string | null,
  options: LayoutOptions = {}
): Node<T>[] {
  if (nodes.length === 0) return nodes;

  const opts = { ...defaultOptions, ...options };

  // Build adjacency list
  const adjacency = new Map<string, Set<string>>();
  nodes.forEach((n) => adjacency.set(n.id, new Set()));

  edges.forEach((e) => {
    adjacency.get(e.source)?.add(e.target);
    adjacency.get(e.target)?.add(e.source);
  });

  // BFS to assign layers
  const layers = new Map<string, number>();
  const visited = new Set<string>();

  // Start from root or first node
  const startId = rootId || nodes[0].id;
  const queue: { id: string; layer: number }[] = [{ id: startId, layer: 0 }];

  while (queue.length > 0) {
    const { id, layer } = queue.shift()!;
    if (visited.has(id)) continue;

    visited.add(id);
    layers.set(id, layer);

    const neighbors = adjacency.get(id) || new Set();
    neighbors.forEach((neighborId) => {
      if (!visited.has(neighborId)) {
        queue.push({ id: neighborId, layer: layer + 1 });
      }
    });
  }

  // Handle disconnected nodes
  nodes.forEach((n) => {
    if (!layers.has(n.id)) {
      layers.set(n.id, (layers.size > 0 ? Math.max(...layers.values()) : 0) + 1);
    }
  });

  // Group nodes by layer
  const layerGroups = new Map<number, Node<T>[]>();
  nodes.forEach((node) => {
    const layer = layers.get(node.id) || 0;
    if (!layerGroups.has(layer)) {
      layerGroups.set(layer, []);
    }
    layerGroups.get(layer)!.push(node);
  });

  // Position nodes
  return nodes.map((node) => {
    const layer = layers.get(node.id) || 0;
    const nodesInLayer = layerGroups.get(layer) || [];
    const indexInLayer = nodesInLayer.findIndex((n) => n.id === node.id);

    // Center nodes horizontally within each layer
    const layerWidth = nodesInLayer.length * (opts.nodeWidth + opts.nodeSep);
    const startX = -layerWidth / 2;

    return {
      ...node,
      position: {
        x: startX + indexInLayer * (opts.nodeWidth + opts.nodeSep) + opts.nodeWidth / 2,
        y: layer * (opts.nodeHeight + opts.rankSep),
      },
    };
  });
}

/**
 * Check if a node's position has been manually adjusted
 */
export function isPositionLocked(node: Node & { data: { locked?: boolean } }): boolean {
  return node.data.locked === true;
}

/**
 * Preserve locked node positions while applying layout to others
 */
export function applyLayoutPreservingLocked<T extends Record<string, unknown> & { locked?: boolean }>(
  nodes: Node<T>[],
  edges: Edge[],
  layoutFn: (nodes: Node<T>[], edges: Edge[]) => Node<T>[]
): Node<T>[] {
  // Separate locked and unlocked nodes
  const lockedNodes = nodes.filter((n) => n.data.locked);
  const unlockedNodes = nodes.filter((n) => !n.data.locked);

  // Apply layout only to unlocked nodes
  const layoutedUnlockedNodes = layoutFn(unlockedNodes, edges);

  // Merge back with locked nodes (keeping original positions)
  const nodeMap = new Map<string, Node<T>>();
  layoutedUnlockedNodes.forEach((n) => nodeMap.set(n.id, n));
  lockedNodes.forEach((n) => nodeMap.set(n.id, n));

  // Return in original order
  return nodes.map((n) => nodeMap.get(n.id)!);
}
